// HAND-WRITTEN. Policy, like the denylist in `hostRpcNames.ts`, so `yarn rpc:refresh` does not own it.
//
// Which SDK tool runs a host procedure. Listed here: `giraffe_sdk_read` — NOTHING IS SAVED TO THE
// PROJECT: reads, and changes to what the user is looking at. Everything else: `giraffe_sdk_write`.
// The user grants the two separately in their MCP client, so a write on this list is a write the
// user never agreed to.
//
// ⚠️ AN ALLOWLIST, the way `safety/` is one: anything not named here is a write, including every
// procedure a future `yarn rpc:refresh` adds. A name prefix decides nothing — `get*` is a naming
// habit of the current build, not a contract (P1).
//
// A `view` entry carries its reason AND the host-source line it was verified against.
// *Verified against the current build* at giraffe-four@ac3b38d2b5 (2026-10-08), not a contract:
// re-read on refresh. An entry or bullet citing `@50cad4903` was re-read at that commit (2026-10-12).
// Paths are under `libs/`.
//
// Left out on purpose, so they are writes:
// - `getUserDrawnPolygon`, `getUserDrawnLineString`, `getUserDrawnPoint`: each reveals the map
//   panel first, and a layout change is POSTed to the project (`sketch/src/lib/DrawNew/giDrawNew.tsx:102`,
//   `panels/src/lib/panelStateManager.ts:771`).
// - `disableIsolateMode`: saves nothing, but exits isolation even mid block-edit and drops the
//   unsaved edit (`gi-js/src/lib/exports/misc.ts:572`, `ui-state/src/lib/uiStateManager.ts:947`).
// - `changeLayerVisibility`, `changeLayerOpacity`: *documented* (the `setDrawingLayerLocked`
//   JSDoc) as "written to the backend when the layer tree is next saved".
// - `addIframePopup`: saves nothing, but opens a frame onto any http(s) URL inside Giraffe, sandboxed
//   per app (`gi-js/src/lib/exports/map.ts:333-345` @50cad4903), so it is not a display change this surface can
//   vouch for.
// - `getProjectBundle`: asks the user before loading any project not already allowed
//   (`gi-js/src/lib/workspace.ts:129` @50cad4903), and a read must not wait on a human.
// - `getUserClaimsJwt` (the user's identity token), `getProjectAttachmentPrompt` (uploads),
//   `navigateTo` and `openProject` (leave the project).

import { DENIED_HOST_RPC_NAMES, HOST_RPC_NAMES } from "./hostRpcNames";
import { HOST_RPC_SIGNATURES } from "./hostRpcSignatures";

export const SDK_READ = "giraffe_sdk_read";
export const SDK_WRITE = "giraffe_sdk_write";
export type SdkTool = typeof SDK_READ | typeof SDK_WRITE;

type Entry = { readonly kind: "read" } | { readonly kind: "view"; readonly reason: string };

const READ: Entry = { kind: "read" };
const view = (reason: string): Entry => ({ kind: "view", reason });

const ALLOWLIST: ReadonlyMap<string, Entry> = new Map<string, Entry>([
  // ── Pure reads and computation. Every `fetch` among them is a GET. ──
  ["featureArea", READ],
  ["fromProjected", READ],
  ["toProjected", READ],
  ["evaluateFeatures", READ],
  ["stackFeatures", READ],
  ["validateLensStyle", READ],
  ["fetchContentPacks", READ],
  ["fetchPackContents", READ],
  ["fetchProjectDetails", READ],
  ["fetchProjectFiles", READ],
  ["fetchUserFiles", READ],
  ["fetchVistas", READ],
  ["fetchWorkspaceDetails", READ],
  ["getAnalyticsResult", READ],
  ["getAppPanels", READ],
  ["getBlock", READ],
  ["getBlockEditStatus", READ],
  ["getBlocks", READ],
  ["getFeatureState", READ],
  ["getFlowHandleNodeTypes", READ],
  ["getGeoTiff", READ],
  ["getGltf", READ],
  ["getGroupEditMode", READ],
  ["getIsolateMode", READ],
  ["getIsolatedFeatures", READ],
  ["getLayerContents", READ],
  ["getLayerPermission", READ],
  ["getLensedFeatureMap", READ],
  ["getMapBounds", READ],
  ["getMapCamera", READ],
  ["getMapStyle", READ],
  ["getMapboxLayerIdsForLayer", READ],
  ["getNode", READ],
  ["getParameterNode", READ],
  ["getParameterNodeDefaultTransform", READ],
  ["getProjectApp", READ],
  ["listHostedApps", READ], // a GET of the workspace's hosted apps, `gi-js/src/lib/exports/project.ts:258` @50cad4903
  ["getProjectFlows", READ],
  ["getProjectUsages", READ],
  ["getProjects", READ],
  ["getQueriedFeature", READ],
  ["getScenarioFeatures", READ],
  ["getScenarios", READ],
  ["getSdkRenderEnabled", READ],
  ["getSelectableProjectFeatures", READ],
  ["getSelectedFeatures", READ],
  ["getSourceLayerDetails", READ],
  ["getTeamList", READ],
  ["getTempUrl", READ],
  ["getTerrainMeshes", READ],
  ["getTiles", READ],
  ["getUrlParams", READ],
  ["getUsage", READ],
  ["getUserData", READ],
  ["getVectorLayerContents", READ],
  ["getVisibleScenarios", READ],
  ["queryRenderedFeatures", READ],
  // ── Prompting reads: the user draws or picks, the result comes back, no section is created. ──
  ["drawPoint", READ], // `sketch/src/lib/giGetPoint.tsx:130`
  ["getLassoShape", READ], // `sketch/src/lib/DrawCommands/getFreehand.tsx:44`
  ["getLassoedLensedFeatures", READ], // `gi-js/src/lib/exports/select.ts:118`
  ["getLassoedProjectFeatures", READ], // `gi-js/src/lib/exports/select.ts:102`
  ["pickRawSections", READ], // `sketch/src/lib/giGetFeatures.tsx:318`, returns clones
  // Opens the selector with upload off by default (`reticulated/src/ProjectAttachmentSelector.tsx:50,97`).
  ["selectProjectAttachmentPrompt", READ],
  // ── View: changes what is on screen, saves nothing. ──
  ["flyTo", view("moves the camera; nothing is saved")], // `gi-js/src/lib/exports/map.ts:37`
  ["fitBounds", view("moves the camera; nothing is saved")], // `map.ts:155`
  // Framing a rectangle switches the map to 2D and moves the camera (`misc.ts:311`,
  // `gi-map/src/lib/vistas/vistaUtils.ts:12`, @50cad4903); without one it captures the view as it is.
  ["getPng", view("a screenshot; framing a rectangle moves the camera; nothing is saved")],
  // Frame for the capture, then restore the user's camera and top view in a `finally`
  // (`gi-js/src/lib/exports/misc.ts#captureAlignedPng`).
  ["getPng2D", view("a screenshot; the camera returns after; nothing is saved")],
  ["getPng3D", view("a screenshot; the camera returns after; nothing is saved")],
  ["setMapCamera", view("moves the camera; nothing is saved")], // `map.ts:115`
  // May enter group-edit UI; a no-op on a read-only project (`map.ts:207`, `ui-state/src/lib/uiStateManager.ts:638`).
  ["setSelectedFeatures", view("changes the selection; nothing is saved")],
  ["setHighlightedFeatures", view("highlights features on the map; nothing is saved")], // `map.ts:217`
  // Both refuse during a block edit (`gi-js/src/lib/exports/misc.ts:522`, `:549`).
  ["setIsolatedFeatures", view("isolates features on screen; nothing is saved")],
  ["clearIsolatedFeatures", view("ends isolation on screen; nothing is saved")],
  ["enableIsolateMode", view("isolate mode is a display mode; nothing is saved")], // `misc.ts:577`
  // *Documented*: "exists only for the current session/client". Written with writeToBackend=false;
  // a later save strips `GIRTMP-*` items (`layer-api/src/utils/temp.ts:94`,
  // `layer-core/src/LayerStateManager.ts:200`).
  ["addTempLayer", view("a temporary layer, for this session only")],
  ["addTempLayerGeoJSON", view("a temporary layer, for this session only")], // `temp.ts:15`
  ["updateTempLayerGeoJSON", view("a temporary layer, for this session only")], // `temp.ts:48`
  ["removeTempLayer", view("a temporary layer, for this session only")], // `temp.ts:163`
  ["addHtmlPopup", view("a popup on the map; nothing is saved")], // `map.ts:284`
  ["clearSDKPopup", view("removes a popup; nothing is saved")], // `map.ts:367`
  ["setFeatureState", view("map render state; nothing is saved")], // `map.ts:377`
  ["removeFeatureState", view("map render state; nothing is saved")], // `map.ts:405`
  // *Documented*: disabling "restores features to their project state".
  ["setSdkRender", view("render overrides on screen; the project is untouched")], // `gi-map/src/lib/giraffeMap.ts:246`
  ["disableSdkRender", view("ends render overrides; the project is untouched")], // `render.ts:25`
  ["loadImage", view("loads an image into the map style for this session")], // `map.ts:452`
  // The next eight are overridden in `builtin-apps/src/lib/Parent/SDKDispatcher.ts:89-163`.
  ["enableMapCamera", view("what the host pushes to this frame; nothing is saved")],
  ["disableMapCamera", view("what the host pushes to this frame; nothing is saved")],
  ["enableMapHover", view("what the host pushes to this frame; nothing is saved")],
  ["disableMapHover", view("what the host pushes to this frame; nothing is saved")],
  ["enableMapContentEvents", view("what the host pushes to this frame; nothing is saved")],
  ["disableMapContentEvents", view("what the host pushes to this frame; nothing is saved")],
  ["addMapboxEventListener", view("a map listener for this session; nothing is saved")],
  ["removeMapboxEventListener", view("a map listener for this session; nothing is saved")],
]);

/** The map itself, for `allowlistProblems` in the test. */
export const READ_ONLY_ALLOWLIST: ReadonlyMap<string, Entry> = ALLOWLIST;

/** Which SDK tool runs a procedure. Unlisted — including a name the host does not register — is the write tool. */
export function sdkToolFor(name: string): SdkTool {
  return ALLOWLIST.has(name) ? SDK_READ : SDK_WRITE;
}

/**
 * Why an allowlist is unsound, one line per entry, or empty. Exported so the test can prove this
 * fires on a bad entry rather than trusting that an empty result means a clean list.
 */
export function allowlistProblems(entries: ReadonlyMap<string, Entry>): readonly string[] {
  const problems: string[] = [];
  for (const name of entries.keys()) {
    if (!HOST_RPC_NAMES.has(name)) problems.push(`${name}: not registered by the host snapshot`);
    else if (DENIED_HOST_RPC_NAMES.has(name))
      problems.push(`${name}: denied, so it cannot be open`);
    else if (HOST_RPC_SIGNATURES.get(name)?.facts.asksUser === true) {
      problems.push(`${name}: asks the user to confirm, so it is not a read`);
    }
  }
  return problems;
}
